// 「统计助教」的主题目录 —— 按统计学知识体系组织
//
// 与 rStatsFaq.ts 的分工：
// - rStatsFaq：面向编辑器内的「代码解释」，问题都是"在R中怎么做"
// - 本文件：面向「统计助教」，条目是**统计学主题本身**（概念、原理、公式），
//   回答重点是讲清楚统计含义并给出计算公式，而不是给一段R代码

export interface StatsTopicCategory {
  title: string;
  topics: string[];
}

export const STATS_TOPIC_CATEGORIES: StatsTopicCategory[] = [
  {
    title: '集中趋势与离散程度',
    topics: [
      '均值', '中位数', '众数', '加权均值', '全距与四分位距',
      '方差', '标准差', '变异系数', '偏度', '峰度', '标准分(z分数)'
    ]
  },
  {
    title: '数据分布与图形',
    topics: [
      '频数分布表', '直方图', '箱线图', '茎叶图', '累积频率曲线',
      '正态分布的图形特征', '偏态分布', '异常值的判断标准'
    ]
  },
  {
    title: '概率基础',
    topics: [
      '随机事件与样本空间', '概率的三种解释', '概率的加法法则', '概率的乘法法则',
      '条件概率', '事件的独立性', '贝叶斯定理', '排列与组合'
    ]
  },
  {
    title: '概率分布',
    topics: [
      '随机变量', '期望与方差', '二项分布', '泊松分布', '正态分布',
      '标准正态分布', 't分布', '卡方分布', 'F分布', '分布之间的关系'
    ]
  },
  {
    title: '抽样与抽样分布',
    topics: [
      '总体与样本', '随机抽样的方法', '抽样误差', '样本均值的抽样分布',
      '标准误', '中心极限定理', '大数定律', '样本量对抽样分布的影响'
    ]
  },
  {
    title: '参数估计与置信区间',
    topics: [
      '点估计与区间估计', '估计量的无偏性', '估计量的有效性', '均值的置信区间',
      '比例的置信区间', '置信水平的正确含义', '置信区间的宽度受什么影响'
    ]
  },
  {
    title: '假设检验原理',
    topics: [
      '原假设与备择假设', '检验统计量', 'p值的含义', '显著性水平α',
      '第一类错误', '第二类错误', '单尾检验与双尾检验',
      '统计显著与实际显著', '多重比较问题', '等价性检验'
    ]
  },
  {
    title: '均值比较',
    topics: [
      '单样本Z检验', '单样本t检验', '独立样本t检验', '配对样本t检验',
      '方差齐性假设', 'Welch校正', 't检验的前提条件'
    ]
  },
  {
    title: '方差分析',
    topics: [
      '方差分析的基本原理', '组间变异与组内变异', 'F比率', '单因素方差分析',
      '事后多重比较', '双因素方差分析', '主效应', '交互作用',
      '重复测量方差分析', '球形假设', '协方差分析'
    ]
  },
  {
    title: '相关分析',
    topics: [
      '协方差', 'Pearson积差相关', 'Spearman等级相关', '点二列相关',
      '偏相关与半偏相关', '相关系数的显著性检验', '相关与因果的区别',
      '相关系数的影响因素'
    ]
  },
  {
    title: '回归分析',
    topics: [
      '最小二乘法', '回归方程与残差', '决定系数R方', '回归系数的显著性检验',
      '多元线性回归', '标准化回归系数', '多重共线性', '回归的前提假设',
      '分层回归', '逻辑回归'
    ]
  },
  {
    title: '分类数据分析',
    topics: [
      '列联表', '卡方拟合优度检验', '卡方独立性检验', '期望频数的计算',
      'Fisher精确检验', 'McNemar检验', 'Phi系数与Cramér\'s V'
    ]
  },
  {
    title: '非参数检验',
    topics: [
      '参数与非参数方法的区别', '秩转换的原理', '符号检验',
      'Mann-Whitney U检验', 'Wilcoxon符号秩检验',
      'Kruskal-Wallis检验', 'Friedman检验', '非参数检验的效应量'
    ]
  },
  {
    title: '效应量与统计功效',
    topics: [
      '效应量的意义', 'Cohen\'s d', '相关类效应量r', 'eta方与偏eta方',
      'omega方', '统计功效', '功效分析的四要素', '样本量估算',
      '效应量与样本量的关系'
    ]
  },
  {
    title: '心理测量与信效度',
    topics: [
      '经典测验理论', '真分数与测量误差', '信度的类型', '重测信度',
      '复本信度', '内部一致性系数α', 'McDonald\'s ω', '效度的类型',
      '内容效度', '结构效度', '效标关联效度', '评分者一致性(kappa与ICC)'
    ]
  },
  {
    title: '因素分析与潜变量',
    topics: [
      '探索性因素分析', '公因子与特殊因子', '因子载荷', '共同度',
      '特征值与碎石图', '因子旋转', '主成分分析与因素分析的区别',
      '验证性因素分析', '模型拟合指标', '测量不变性'
    ]
  },
  {
    title: '高级统计模型',
    topics: [
      '多层线性模型', '组内相关系数ICC', '固定效应与随机效应',
      '中介效应', '调节效应', '有调节的中介', '结构方程模型',
      '路径分析', '潜变量增长模型', '交叉滞后模型'
    ]
  },
  {
    title: '贝叶斯与研究综合',
    topics: [
      '频率派与贝叶斯派的区别', '先验分布与后验分布', '贝叶斯因子',
      '可信区间与置信区间', '元分析的基本思想', '效应量的合并',
      '异质性I²', '发表偏倚', '可重复性危机'
    ]
  }
];

export const STATS_TOPIC_TOTAL = STATS_TOPIC_CATEGORIES.reduce(
  (sum, category) => sum + category.topics.length,
  0
);

/** 主题条目是名词短语，直接发送不像一个问题，因此包装成讲解请求 */
export const buildTopicRequest = (topic: string): string => `请讲解「${topic}」`;
